/**
 * Precedent: APR Framework Management — `apps/portal/web/src/pages/apr/settings/FrameworksList.tsx`
 * Cloned, not imported (DECISIONS.md D-003). Nothing under pages/apr/ or
 * pages/master-data/ is modified by this module.
 *
 * SCREEN_MAP #1 — INI-01-03. The row model maps across as that row says: name, description,
 * type and status plus derived counts; the status chip (`:385-392`) and the Completeness bar
 * (`:395-409`) are cloned and the bar is repointed at **§6.1's balance, which is the baseline
 * gate**; the guarded-delete flow (`:105-121`) surfaces the server's refusal verbatim.
 *
 * WHERE THE DATA MADE THE SCREEN DIFFER — and these are DATA differences, which is the only
 * kind SCREEN_MAP permits (its "How to read this table"):
 *
 *   · An initiative has no `code`, so the second line of the first cell carries the two fields
 *     that do identify its world — fiscal year and currency (§2.3.B2 names both).
 *   · THREE numeric columns became TWO. The precedent counts dimensions, components and
 *     questions; API_CONTRACT §4.3 computes exactly two counts for an initiative
 *     (`capability_model_count`, `assessment_unit_count`). A third column would need a third
 *     number, and inventing data is worse than a narrower table.
 *   · The chip row under the summary holds the operating mode and the AI switch instead of a
 *     framework's answering aspects. Same row, same chip; §1.9.2's measured pairs.
 *   · THERE IS NO CLONE ACTION. The precedent's `openClone` (`:306-313`) posts to
 *     `/api/frameworks/:id/clone`; **API_CONTRACT has no equivalent for an initiative**, and
 *     faking one in the browser (create, then copy a model into it) would put a second
 *     definition of what "a copy of an initiative" means into the client. Reported, not
 *     improvised.
 *   · THERE IS NO FORCE-DELETE. The precedent offers one when the refusal mentions models
 *     (`:377-379`); §4.7 has no `?force=true` and refuses a baselined initiative outright.
 *     The refusal is shown as the server worded it.
 *
 * Create and Edit open the right-side PANEL rather than a centred dialog, because that is what
 * SCREEN_MAP #2 names as the precedent for an initiative's detail — the APR Model panel, which
 * carries exactly this shape: a named header object, a referenced definition, a composed
 * membership list, and a status that gates editing.
 *
 * SO DOES SETUP, since 2026-10-05 — owner: *"when I hit setting, I need to see right panel
 * design. Sames with Edit button."* The ⚙️ row action no longer navigates away from the list; it
 * opens the readiness checklist as a panel over it (`InitiativeSetupPanel`), at the SINGLE width
 * `chrome/panelWidths.css` makes the default. The standalone `/…/:id/setup` route is untouched
 * and still renders the same checklist.
 *
 * NOTHING ELSE ON THIS SCREEN MOVED. Owner, the same day: *"I like the design of
 * .../erp_consolidation/initiatives. DO not make more changes on that screen."* The table, its
 * columns, its row glyphs, the toolbar, the count line, the empty state, the delete confirmation
 * and every string are as they were; the only change is what two buttons open.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErpScreen } from '../ErpScreen';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { erpApi } from '../api';
import { type ErpContext, type Initiative } from '../types';
import { canAdministerInitiatives } from '../roles';
import { CHIP_INFO, CHIP_NEUTRAL, CHIP_SUCCESS } from '../chips';
import { InitiativeDetailPanel } from './InitiativeDetailPanel';
import { InitiativeSetupPanel, SetupHelp } from './InitiativeSetup';
import './InitiativesList.css';

type Toast = { type: 'success' | 'error'; message: string } | null;

/* ---------------------------------------------------------------------------
 * Contextual help — AUTHORED, not cloned.
 *
 * UI_CONVENTIONS §3.7: this module has no source app to lift wording from, so the copy is
 * written from `BUILD_INSTRUCTIONS.md` §5 (the requirement IDs) and §6 (the rules), with the
 * tone of `settings/ScoringModel.tsx:17-41` and the honesty of `ScoringAuditTrail.tsx:51-52`
 * about what the screen does not yet do.
 * ------------------------------------------------------------------------- */

const InitiativesHelp: Help = {
  title: 'Initiatives',
  icon: '🧭',
  purpose:
    'An <strong>initiative</strong> is one consolidation exercise: the business units in scope, the capability model everything is weighed against, the systems being compared, and the world it is all computed in. Everything else in this module hangs off one of these.',
  doHere: [
    'Click <strong>+ Create Initiative</strong> to name one and choose its <strong>operating mode</strong>. The mode decides where a system definition comes from, so it is chosen once and <strong>frozen when the initiative is baselined</strong>.',
    'Click an initiative <strong>name</strong> to open its detail panel and edit its header, type, status, currency, fiscal year and whether AI is enabled.',
    'Use <strong>Model</strong> to open its capability model and set the weights.',
    'Use <strong>Setup</strong> to see what is still outstanding before it can be baselined.',
    'Watch <strong>Weights</strong>: it reads 100% only when <strong>every level of the capability model totals exactly 100.0%</strong> — which is the condition for baselining at all.',
  ],
  fits: 'ERP Consolidation &rarr; Initiatives',
  firstTime:
    'Create one initiative, add the business units it covers, then build its capability model until every sibling set totals exactly 100.0%.',
  sections: [
    {
      type: 'defs',
      label: 'Initiative state',
      items: [
        {
          term: 'Baselined',
          color: '#8c3a00',
          def: 'The snapshot behind every later score has been taken. The <strong>operating mode can no longer change</strong>, and the initiative cannot be deleted.',
        },
        {
          term: 'Draft',
          color: '#3c4043',
          def: 'Still being set up — fully editable. This is the normal state, not an error: business units, the capability model and the systems are worked on in parallel.',
        },
      ],
    },
    {
      type: 'defs',
      label: 'Operating mode',
      items: [
        {
          term: 'APR connected',
          color: '#0842a0',
          def: 'System definitions are read from the Application Portfolio Rationalization module, <strong>read only</strong>. Available only where that module is licensed.',
        },
        {
          term: 'Standalone',
          color: '#3c4043',
          def: "System definitions are this module's own. Always available, and the right choice for systems outside APR's portfolio.",
        },
      ],
      note: 'The mode is chosen when the initiative is created and <strong>frozen at baseline</strong>: changing it afterwards would leave every published score rendering against systems from a different source.',
    },
    {
      type: 'html',
      label: 'What this screen does not do yet',
      html: 'There is no <strong>Clone</strong>, because the API has no definition of what a copy of an initiative is yet — raised rather than guessed at. Searching and filtering are not here either; the list shows every initiative on this install.',
    },
  ],
};

/* ---------------------------------------------------------------------------
 * Derived helpers — the precedent's `isLocked` / `completeness` / `summaryLine`
 * (`FrameworksList.tsx:155-165`), repointed at an initiative.
 * ------------------------------------------------------------------------- */

/** The precedent's `apr_model_count > 0`. Here: the baseline, which is what locks a row. */
const isBaselined = (initiative: Initiative): boolean => initiative.baselined_at !== null;

const summaryLine = (initiative: Initiative): string => {
  const models = initiative.capability_model_count;
  const units = initiative.assessment_unit_count;
  const base = `${models} capability model${models === 1 ? '' : 's'}, ${units} assessment unit${units === 1 ? '' : 's'}`;
  const description = (initiative.description ?? '').trim();
  return description ? `${base} — ${description}` : base;
};

export function InitiativesList() {
  const navigate = useNavigate();
  const { setRoot, rootStyle } = useScreenLock();

  const [context, setContext] = useState<ErpContext | null>(null);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast>(null);

  /* The right-side panel: create (null id) or open on one initiative. */
  const [panel, setPanel] = useState<{ initiativeId: string | null } | null>(null);

  /*
   * The SETUP panel — the readiness checklist the ⚙️ row action opens. Owner, 2026-10-05:
   * *"when I hit setting, I need to see right panel design. Sames with Edit button."*
   *
   * It holds the whole row rather than an id, because the panel header names the initiative and
   * the list already has that name: loading it again to print a string it is holding would make
   * the header arrive after the panel did.
   */
  const [setup, setSetup] = useState<Initiative | null>(null);

  /* Delete modal — the precedent's guarded flow, minus the force path it has no endpoint for. */
  const [deleting, setDeleting] = useState<Initiative | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      /*
       * `limit: 200` is §1.10's maximum, clamped silently above it. The precedent paginates
       * nothing and neither does any APR table (§4.5), so rather than inventing a pager this
       * asks for one page of the largest size the contract allows and SAYS so in the count
       * line when there is more — text, not a new control.
       */
      const [contextPayload, listPayload] = await Promise.all([
        erpApi.context(),
        erpApi.initiatives.list({ limit: 200 }),
      ]);
      setContext(contextPayload.context);
      setInitiatives(listPayload.initiatives);
      setTotal(listPayload.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load initiatives');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /* auto-dismiss toast — `FrameworksList.tsx:214-219`, 3000 ms. */
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const notifyOk = (message: string) => setToast({ type: 'success', message });
  const notifyErr = (message: string) => setToast({ type: 'error', message });

  const canAdminister = canAdministerInitiatives(context);

  /*
   * THE PLATFORM ROUTE, NOT A SOURCE ONE — and never `/apr/...` (UI_CONVENTIONS §18.11).
   * The capability model editor reads its initiative from the query string, which is
   * `FrameworkBuilder.tsx:933-949`'s own pattern: the URL is the single source of truth, so
   * the load effect that keys on it does the reload and a shared link opens the same model.
   */
  const openModel = (initiative: Initiative) =>
    navigate(`/erp_consolidation/capability-model?initiative=${encodeURIComponent(initiative.id)}`);

  /*
   * ONE RIGHT-SIDE PANEL AT A TIME, which is why both openers go through a function rather than
   * calling their own setter. The panels are both anchored to the right edge at the same width,
   * and neither overlay dims or swallows clicks (D-017 — the list behind stays readable and
   * usable), so without this a click on the list while one is open would stack the second
   * exactly on top of the first. Nothing visible on the screen changes; only which of the two
   * is mounted.
   *
   * The standalone Setup screen is NOT removed: `/erp_consolidation/initiatives/:id/setup` is
   * still routed by `ErpConsolidationModule.tsx:164` and still renders `InitiativeSetup`, so any
   * link to a specific initiative's readiness keeps working.
   */
  const openDetail = (initiativeId: string | null) => {
    setSetup(null);
    setPanel({ initiativeId });
  };

  const openSetup = (initiative: Initiative) => {
    setPanel(null);
    setSetup(initiative);
  };

  const performDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    setDeleteError(null);
    try {
      await erpApi.initiatives.remove(deleting.id);
      notifyOk('Initiative deleted');
      setDeleting(null);
      await load();
    } catch (err) {
      /* The server's own wording, shown unaltered — `FrameworksList.tsx:105-121`. */
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete initiative');
    } finally {
      setDeletingBusy(false);
    }
  };

  /* --- render helpers ----------------------------------------------------- */

  const renderStatusBadge = (initiative: Initiative) => {
    const baselined = isBaselined(initiative);
    return (
      <span className={`fwl-status ${baselined ? 'fwl-status-locked' : 'fwl-status-design'}`}>
        {baselined ? 'Baselined' : initiative.status}
      </span>
    );
  };

  /**
   * The Completeness bar, repointed at §6.1's balance (SCREEN_MAP #1).
   *
   * Three states, and the middle one is the contract's own instruction: `weights_balanced` is
   * `null` where there is no model at all, "so the bar can render 'no model yet' rather than
   * '0%'" (§4.3). A deficit cannot be shown here because the list payload carries a boolean,
   * not a total — the numbers live on the editor, which is where §6.1 puts them.
   */
  const renderWeights = (initiative: Initiative) => {
    if (initiative.weights_balanced === null) {
      return (
        <div className="fwl-completeness">
          <span className="fwl-bar-pct is-short">No model yet</span>
        </div>
      );
    }
    const balanced = initiative.weights_balanced;
    return (
      <div className="fwl-completeness">
        <div className="fwl-bar-track">
          <div className="fwl-bar-fill" style={{ width: balanced ? '100%' : '0%' }} />
        </div>
        <span className={`fwl-bar-pct ${balanced ? 'is-full' : 'is-short'}`}>
          {balanced ? '100%' : 'Not balanced'}
        </span>
      </div>
    );
  };

  const renderActions = (initiative: Initiative) => {
    const baselined = isBaselined(initiative);
    return (
      <div className="fwl-row-actions">
        <button
          type="button"
          className="fwl-act"
          title="Capability model"
          aria-label="Capability model"
          onClick={() => openModel(initiative)}
        >
          📋
        </button>
        <button
          type="button"
          className="fwl-act"
          title="Setup checklist"
          aria-label="Setup checklist"
          onClick={() => openSetup(initiative)}
        >
          ⚙️
        </button>
        {/*
          HIDDEN, NOT DISABLED (UI_CONVENTIONS §17.3, `ScoringModel.tsx:289`): a control a
          role may not use is not rendered at all. The server re-checks either way (§1.8).

          Edit and Delete also disappear once the initiative is BASELINED, which is the
          precedent's own rule for a locked framework (`FrameworksList.tsx:429-462`) and the
          server's (§4.7 refuses the delete; §4.6 refuses a mode change).
        */}
        {canAdminister && !baselined && (
          <button
            type="button"
            className="fwl-act"
            title="Edit initiative"
            aria-label="Edit initiative"
            onClick={() => openDetail(initiative.id)}
          >
            ✏️
          </button>
        )}
        {canAdminister && !baselined && (
          <button
            type="button"
            className="fwl-act fwl-act--danger"
            title="Delete initiative"
            aria-label="Delete initiative"
            onClick={() => {
              setDeleting(initiative);
              setDeleteError(null);
            }}
          >
            🗑️
          </button>
        )}
      </div>
    );
  };

  const renderModeChips = (initiative: Initiative) => {
    const mode =
      initiative.operating_mode === 'apr_connected'
        ? { label: 'APR connected', pair: CHIP_INFO }
        : { label: 'Standalone', pair: CHIP_NEUTRAL };
    const ai = initiative.ai_enabled
      ? { label: 'AI on', pair: CHIP_SUCCESS }
      : { label: 'AI off', pair: CHIP_NEUTRAL };
    return (
      <div className="fwl-aspects" title="How this initiative is configured">
        {[mode, ai].map((chip) => (
          <span
            key={chip.label}
            className="fwl-aspect-chip"
            style={{ color: chip.pair.fg, backgroundColor: chip.pair.bg }}
          >
            {chip.label}
          </span>
        ))}
        {initiative.operating_mode_frozen_at && (
          <span className="fwl-aspects-count">mode frozen at baseline</span>
        )}
      </div>
    );
  };

  return (
    <ErpScreen>
      <div className="page fwl-page" ref={setRoot} style={rootStyle}>
        <PageHeader
          title="Initiatives"
          subtitle="One consolidation exercise: its business units, its capability model, its systems and its operating mode"
        />
        {/*
          TWO HELP TABS, and this is the ONE addition to this otherwise frozen screen — flagged
          rather than slipped in. The owner's instruction about this screen is *"I like the design of
          .../erp_consolidation/initiatives. DO not make more changes on that screen"*, and a second
          tab in the help drawer is visible.

          It is here because ⚙️ now opens Initiative Setup as a right-side panel (D-017), and a panel
          cannot carry its own help launcher: this screen's is pinned in the same fixed corner, so
          two would overlap. The setup checklist's "How this works" content already existed and would
          otherwise be reachable only by typing the standalone URL. `HelpPanel` supports the tab strip
          natively (`chrome/HelpPanel.tsx:21-23`), so nothing new was built — the second pane is the
          same object the standalone screen passes.

          NOTHING ELSE ON THIS SCREEN CHANGED: not the layout, not the table, not the columns, not the
          row icons, not the wording.
        */}
        <HelpPanel helps={[InitiativesHelp, SetupHelp]} />

        <div className="fwl-container">
          <div className="fwl-toolbar">
            {canAdminister ? (
              <button type="button" className="btn btn-primary" onClick={() => openDetail(null)}>
                + Create Initiative
              </button>
            ) : (
              <span />
            )}
            <span className="fwl-count">
              {total > initiatives.length
                ? `${initiatives.length} of ${total} initiatives shown`
                : `${initiatives.length} initiative${initiatives.length === 1 ? '' : 's'}`}
            </span>
          </div>

          {loading ? (
            <div className="fwl-state">Loading initiatives…</div>
          ) : error ? (
            <div className="fwl-state fwl-state-error">
              <p>⚠️ {error}</p>
              <button type="button" className="btn btn-primary" onClick={() => void load()}>
                Retry
              </button>
            </div>
          ) : initiatives.length === 0 ? (
            <div className="fwl-state">
              <p>No initiatives yet — create one.</p>
              {canAdminister && (
                <button type="button" className="btn btn-primary" onClick={() => openDetail(null)}>
                  + Create Initiative
                </button>
              )}
            </div>
          ) : (
            <div className="fwl-table-wrap">
              <table className="fwl-table">
                <thead>
                  <tr>
                    <th>Initiative</th>
                    <th>Status</th>
                    <th className="fwl-num">Models</th>
                    <th className="fwl-num">Units</th>
                    <th className="fwl-completeness-col">Weights</th>
                    <th className="fwl-actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {initiatives.map((initiative, idx) => {
                    const accent = idx === 0 || isBaselined(initiative);
                    return (
                      <tr key={initiative.id} className={accent ? 'fwl-accent' : ''}>
                        <td>
                          <button
                            type="button"
                            className="fwl-name-link"
                            onClick={() => openDetail(initiative.id)}
                            title="Open this initiative"
                          >
                            {initiative.name}
                          </button>
                          <div className="fwl-code">
                            {[
                              initiative.fiscal_year,
                              initiative.currency_code,
                              initiative.initiative_type,
                            ]
                              .filter((part) => part)
                              .join(' · ') || 'N/A'}
                          </div>
                          <div className="fwl-desc">{summaryLine(initiative)}</div>
                          {renderModeChips(initiative)}
                        </td>
                        <td>{renderStatusBadge(initiative)}</td>
                        <td className="fwl-num fwl-stat">{initiative.capability_model_count}</td>
                        <td className="fwl-num fwl-stat">{initiative.assessment_unit_count}</td>
                        <td className="fwl-completeness-col">{renderWeights(initiative)}</td>
                        <td>{renderActions(initiative)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {panel && (
          <InitiativeDetailPanel
            initiativeId={panel.initiativeId}
            context={context}
            onClose={() => setPanel(null)}
            onSaved={(message) => {
              notifyOk(message);
              void load();
            }}
            onError={notifyErr}
          />
        )}

        {/*
          SETUP, as a right-side panel over this list rather than a screen you navigate to.
          Owner, 2026-10-05: *"when I hit setting, I need to see right panel design."*

          The readiness checklist is unchanged — same conditions, same banner, same gated
          Baseline action — and `InitiativeSetup.tsx` renders exactly one copy of it for both
          this panel and the standalone route, which is still there.

          `onBaselined` re-reads the list because baselining removes a row's Edit and Delete
          actions (§4.7 refuses the delete, §4.6 the mode change): without it the row would go
          on offering controls the server now refuses.
        */}
        {setup && (
          <InitiativeSetupPanel
            initiativeId={setup.id}
            initiativeName={setup.name}
            onClose={() => setSetup(null)}
            onBaselined={() => void load()}
          />
        )}

        {/* Delete confirm — `FrameworksList.tsx:756-818`, without the force-delete branch. */}
        {deleting && (
          <div
            className="form-modal"
            onClick={(e) => e.target === e.currentTarget && !deletingBusy && setDeleting(null)}
          >
            <div className="form-modal-content fwl-delete-modal">
              <div className="form-modal-header">
                <h2>Delete Initiative</h2>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeleting(null)}
                  aria-label="Close dialog"
                  disabled={deletingBusy}
                >
                  ✕
                </button>
              </div>
              <div className="customer-form">
                <p>
                  Are you sure you want to delete <strong>{deleting.name}</strong>? Its capability
                  models, business-unit scoping and assessment units go with it. This action cannot
                  be undone.
                </p>
                {deleteError && <div className="fwl-delete-error">⚠️ {deleteError}</div>}
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setDeleting(null)}
                    disabled={deletingBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => void performDelete()}
                    disabled={deletingBusy}
                  >
                    {deletingBusy ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div className={`fwl-toast fwl-toast-${toast.type}`} role="status">
            {toast.type === 'success' ? '✓ ' : '⚠ '}
            {toast.message}
          </div>
        )}
      </div>
    </ErpScreen>
  );
}
